import IResult from "../../models/result/IResult";
import { sendDelete, sendGet, sendPost, sendPut } from "./generic";
import { MEDICAL_INTEGRATION_CONFIGURATION } from "../../constants/Urls";
import MedicalIntegrationConfigModel from "../../models/organizations/MedicalIntegrationConfigModel";
import {
    PmsOfficeModel,
    PmsProcedureTypeModel,
    PmsProviderModel,
    PmsSlotModel,
    PmsPatientModel,
    PmsCodeModel,
    Cloud9ScheduleViewModel,
    PmsCacheCategoryModel,
    PmsCacheClearModel,
    PmsSlotsRequest,
    PmsPatientSearchRequest
} from "../../models/organizations/PmsLookupModels";

export const getMedicalIntegrationConfigsByOrganizationId = (organizationId: string): Promise<IResult<MedicalIntegrationConfigModel[]>> => {
    return sendGet<MedicalIntegrationConfigModel[]>(`${MEDICAL_INTEGRATION_CONFIGURATION}/${organizationId}`);
}

export const createMedicalIntegrationConfig = (model: MedicalIntegrationConfigModel): Promise<IResult<MedicalIntegrationConfigModel>> => {
    return sendPost<MedicalIntegrationConfigModel>(`${MEDICAL_INTEGRATION_CONFIGURATION}`, model);
}

export const updateMedicalIntegrationConfig = (configId: string, model: MedicalIntegrationConfigModel): Promise<IResult<MedicalIntegrationConfigModel>> => {
    return sendPut<MedicalIntegrationConfigModel>(`${MEDICAL_INTEGRATION_CONFIGURATION}/${configId}`, model);
}

export interface AppointmentSummaryRequest {
    applicationIds: string[];
    dateFrom: string;
    dateTo: string;
}

export interface AppointmentSessionMatch {
    organizationId: string;
    appointmentId?: string;
    phoneNumber: string;
    confirmedByVoicify: boolean;
    leftMessageByVoicify: boolean;
}

export interface AppointmentSummaryResponse {
    totalAppointments: number;
    eligibleAppointments?: number;
    successfulOutcomes?: number;
    totalConfirmed: number;
    totalCancelled: number;
    totalReschedules: number;
    totalCheckedOut: number;
    totalLeftMessage: number;
    confirmedByVoicify: number;
    confirmedByVoicifyAndCheckedOut?: number;
    leftMessageByVoicify: number;
    totalMissed: number;
    totalOther?: number;
    confirmedByVoicifyAfterLeftMessage?: number;
    confirmedAfterVoicifyLeftMessage: number;
    missedAfterVoicifyLeftMessage: number;
    messageOutcomeConfirmed?: number;
    messageOutcomeCheckedOut?: number;
    messageOutcomeRescheduled?: number;
    messageOutcomeCancelled?: number;
    messageOutcomeLeftMessage?: number;
    messageOutcomeMissed?: number;
    messageOutcomeOther?: number;
    missedWithSubsequentBooking: number;
    missedWithBookingAfterVoicifyCall: number;
    missedWithoutSubsequentBooking: number;
    missedSubsequentBookingUnknown: number;
    missedPatientsCalled?: number;
    missedPatientsWithBooking?: number;
    missedPatientsWithVoicifyBooking?: number;
    totalReactivationPatients?: number;
    reactivationPatientsWithBooking?: number;
    reactivationPatientsWithVoicifyBooking?: number;
    reactivationPatientsWithoutBooking?: number;
    reactivationPatientBookingUnknown?: number;
    appointmentSessionMatches?: AppointmentSessionMatch[];
    failedToConnect?: string[];
}

export const getAppointmentSummary = (
    organizationId: string,
    request: AppointmentSummaryRequest
): Promise<IResult<AppointmentSummaryResponse>> => {
    return sendPost<AppointmentSummaryResponse>(
        `${MEDICAL_INTEGRATION_CONFIGURATION}/${organizationId}/appointmentsummary`,
        request
    );
}

// PMS lookups run against the PMS behind a saved configuration
const toQueryString = (params: Record<string, string | boolean | undefined>): string => {
    const query = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== '')
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
        .join('&');
    return query ? `?${query}` : '';
}

const pmsLookupUrl = (configId: string, path: string, params: Record<string, string | boolean | undefined> = {}): string => {
    return `${MEDICAL_INTEGRATION_CONFIGURATION}/${configId}/lookups/${path}${toQueryString(params)}`;
}

export const getPmsOffices = (configId: string): Promise<IResult<PmsOfficeModel[]>> => {
    return sendGet<PmsOfficeModel[]>(pmsLookupUrl(configId, 'offices'));
}

export const getPmsProcedureTypes = (configId: string, officeId: string, onlyBookable: boolean = false): Promise<IResult<PmsProcedureTypeModel[]>> => {
    return sendGet<PmsProcedureTypeModel[]>(pmsLookupUrl(configId, 'procedure-types', { officeId, onlyBookable }));
}

export const getPmsProviders = (configId: string, officeId?: string): Promise<IResult<PmsProviderModel[]>> => {
    return sendGet<PmsProviderModel[]>(pmsLookupUrl(configId, 'providers', { officeId }));
}

export const getPmsAvailableSlots = (configId: string, request: PmsSlotsRequest): Promise<IResult<PmsSlotModel[]>> => {
    return sendGet<PmsSlotModel[]>(pmsLookupUrl(configId, 'slots', { ...request }));
}

// Patient lookups carry PHI, so they are POSTs with the values in the body, never in the URL
export const searchPmsPatients = (configId: string, request: PmsPatientSearchRequest): Promise<IResult<PmsPatientModel[]>> => {
    return sendPost<PmsPatientModel[]>(pmsLookupUrl(configId, 'patients/search'), request);
}

export const getPmsPatient = (configId: string, patientId: string, officeId?: string): Promise<IResult<PmsPatientModel>> => {
    return sendPost<PmsPatientModel>(pmsLookupUrl(configId, 'patients/detail'), { patientId, officeId });
}

export const getDenticonCancellationCodes = (configId: string): Promise<IResult<PmsCodeModel[]>> => {
    return sendGet<PmsCodeModel[]>(pmsLookupUrl(configId, 'denticon/cancellation-codes'));
}

export const getDenticonReferralTypes = (configId: string): Promise<IResult<PmsCodeModel[]>> => {
    return sendGet<PmsCodeModel[]>(pmsLookupUrl(configId, 'denticon/referral-types'));
}

export const getCloud9Employees = (configId: string): Promise<IResult<PmsProviderModel[]>> => {
    return sendGet<PmsProviderModel[]>(pmsLookupUrl(configId, 'cloud9/employees'));
}

export const getCloud9ScheduleViews = (configId: string): Promise<IResult<Cloud9ScheduleViewModel[]>> => {
    return sendGet<Cloud9ScheduleViewModel[]>(pmsLookupUrl(configId, 'cloud9/schedule-views'));
}

// What the server has cached for a configuration, by category (offices, providers, ...). Reads storage only, not the PMS.
export const getPmsCacheSummary = (configId: string): Promise<IResult<PmsCacheCategoryModel[]>> => {
    return sendGet<PmsCacheCategoryModel[]>(pmsLookupUrl(configId, 'cache'));
}

// Deletes one category of the server's cached PMS data for a configuration (e.g. "offices"), or all of it without a
// type, so the next lookup or call pulls it from the PMS again. Nothing in the PMS itself changes.
export const clearPmsLookupCache = (configId: string, type?: string): Promise<IResult<PmsCacheClearModel>> => {
    return sendDelete<PmsCacheClearModel>(pmsLookupUrl(configId, 'cache', { type }));
}
